import { makeStyles } from '@material-ui/core/styles';
import { Handle, Position } from '@xyflow/react';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import {
  GATriggerType,
  NodeType,
  ProviderType,
  TriggerFlowModeType,
  TriggerType,
} from '../../app/types';
import { getCustomFieldDateText } from '../../app/utils';
import IconTrigger from '../../assets/images/Icons/IconTrigger';
import flowImage from '../../assets/images/svg/ic-flow.svg';
import tagImage from '../../assets/images/svg/ic-ma-tag.svg';
import keywordImage from '../../assets/images/svg/ic-ma-text.svg';
import addImage from '../../assets/images/svg/ic-user-add.svg';
import unblockImage from '../../assets/images/svg/ic-user-unlock.svg';
import iconCalendar from '../../assets/images/svg/ic-calendar.svg';
import iconBirth from '../../assets/images/svg/ic_birth.svg';
import iconShopBag from '../../assets/images/svg/ic_EC_ShopBag.svg';
import GAImage from '../../assets/images/svg/ic_googleanalytics_20.svg';
import {
  checkNode,
  setDefaultTemplate,
  setSelectedNode,
  setIsShowSettingBar,
} from '../../features/mainSlice';

const useStyles = makeStyles(
  {
    root: {
      width: 240,
      borderRadius: '12px',
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      border: 0,
    },
    selected: {
      border: `solid 1px ${color.$16}`,
    },
    error: {
      border: `solid 1px ${color.$17}`,
    },
    highlightPath: {
      border: `solid 1px ${color.$3}!important`,
    },
    handle: {
      border: '0 !important',
      borderRadius: '0 !important',
      background: `${color.$3} !important`,
      width: '1px !important',
      height: '1px !important',
      minWidth: '1px !important',
      minHeight: '1px !important',
    },
    title: {
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$18,
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      background: hexToRgba(color.$16, 6),
      '& > svg': {
        marginRight: 2,
      },
    },
    errorTitle: {
      color: color.$17,
      background: hexToRgba(color.$17, 6),
    },
    content: {
      padding: '12px 16px',
    },
    triggger: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 0',
      background: color.$19,
      '& > img': {
        marginRight: 4,
      },
    },
  },
  {
    name: 'Trigger',
  },
);

const TriggerComponent = ({ id, data, isConnectable }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { provider } = useSelector(state => state.superMarket.EC);
  const orgFeatures = useSelector(store => store.organization.features);
  const isErrorNodeIds = useSelector(store => store.ma.isErrorNodeIds);
  const selectedNode = useSelector(store => store.ma.selectedNode);
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const allPathNodes = useSelector(store => store.ma.highlightPathNodes) || [];
  const isPreview = useSelector(store => store.ma.isPreview);
  const isHighlightPath = isPreview && !!allPathNodes.find(n => n?.id === id);
  const { type = 'add' } = data || {};

  const isSelected = selectedNode?.id === id;
  const isError =
    !!~isErrorNodeIds.indexOf(id) &&
    !checkNode({ type: NodeType.TRIGGER, data });

  const triggerStore = {
    [TriggerType.ADD]: { text: t(`trigger.${TriggerType.ADD}`), img: addImage },
    [TriggerType.UNBLOCK]: {
      text: t(`trigger.${TriggerType.UNBLOCK}`),
      img: unblockImage,
    },
    [TriggerType.TAG]: { text: t(`trigger.${TriggerType.TAG}`), img: tagImage },
    [TriggerType.KEYWORD]: {
      text: t(`trigger.${TriggerType.KEYWORD}`),
      img: keywordImage,
    },
    [TriggerType.OTHER]: {
      text: t(`trigger.${TriggerType.OTHER}`),
      img: flowImage,
    },
    [GATriggerType.GAVIEW]: {
      text: t(`trigger.${GATriggerType.GAVIEW}`),
      img: GAImage,
    },
    [GATriggerType.GAREMOVEFROMCART]: {
      text: t(`trigger.${GATriggerType.GAREMOVEFROMCART}`),
      img: GAImage,
    },
    [GATriggerType.GAADDTOCART]: {
      text: t(`trigger.${GATriggerType.GAADDTOCART}`),
      img: GAImage,
    },
    [GATriggerType.GAPURCHASE]: {
      text: t(`trigger.${GATriggerType.GAPURCHASE}`),
      img: GAImage,
    },
  };

  const { text, img } = useMemo(() => {
    switch (type) {
      case TriggerType.BIRTHDAY:
        return { text: t(`birthday.${data?.conditions}`), img: iconBirth };
      case TriggerType.CUSTOM_FIELD_DATE:
        return {
          text: getCustomFieldDateText(t, orgFeatures, data?.conditions),
          img: iconCalendar,
        };
      case ProviderType.SHOPLINE:
      case ProviderType.SHOPIFY:
      case ProviderType.CYBERBIZ:
      case ProviderType.SALESFORCE:
        return { text: t(`ec.${data?.action}`), img: iconShopBag };
      case TriggerFlowModeType.BOT:
      case TriggerFlowModeType.SUPERLINK:
      case TriggerFlowModeType.PROCEDURE:
      case TriggerFlowModeType.API:
        return triggerStore[TriggerType.OTHER];
      default:
        return triggerStore[type];
    }
  }, [data, provider, orgFeatures]);

  const onSelect = () => {
    if (!isSelected) {
      dispatch(setSelectedNode(nodes.find(n => n?.id === id)));
      dispatch(
        setDefaultTemplate({
          isShow: false,
        }),
      );
    }
    dispatch(setIsShowSettingBar(true));
  };

  return (
    <div>
      <Handle
        className={classes.handle}
        type="target"
        position={Position.Top}
        style={{ top: 0 }}
        isConnectable={isConnectable}
      />
      <div
        className={classnames(classes.root, {
          [classes.selected]: isSelected,
          [classes.error]: isError,
          [classes.highlightPath]: isHighlightPath,
        })}
        onClick={onSelect}
      >
        <div
          className={classnames(classes.title, {
            [classes.errorTitle]: isError,
          })}
        >
          <IconTrigger backgroundColor={isError ? color.$17 : color.$18} />
          {t('trigger-node')}
        </div>
        <div className={classes.content}>
          <div className={classes.triggger}>
            <img src={img} alt="triggger" />
            {text}
          </div>
        </div>
      </div>
      <Handle
        className={classes.handle}
        type="source"
        position={Position.Bottom}
        style={{ bottom: 0 }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default TriggerComponent;
